// Port of webapp/src/pages/boardPage/boardPage.tsx — resolves the route params
// to a board + view and hosts the card overlay via the :cardId segment.

import {ReactElement, useCallback, useEffect, useMemo, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {useBoards} from '../store/boards'
import {useViews, selectActiveView, selectViewsOfBoard} from '../store/views'
import {useCards} from '../store/cards'
import {useUsers} from '../store/users'
import {useTeams} from '../store/teams'
import {useSidebar} from '../store/sidebar'
import {loadBoardData} from '../store/initialLoad'
import {buildBoardPath} from '../common/paths'
import Workspace from '../components/workspace'
import CenterPanel from '../components/centerPanel'
import SearchDialog from '../components/searchDialog'
import {Icon} from '../components/common/icons'
import NewBoardDialog from '../components/sidebar/newBoardDialog'

export default function BoardPage(): ReactElement {
    const params = useParams<{teamId?: string, boardId?: string, viewId?: string, cardId?: string}>()
    const navigate = useNavigate()

    const boards = useBoards((state) => state.boards)
    const viewsById = useViews((state) => state.views)
    const cardsById = useCards((state) => state.cards)
    const usersById = useUsers((state) => state.users)
    const storeTeamId = useTeams((state) => state.activeTeamId)
    const setTeamId = useTeams((state) => state.setTeamId)
    const touchRecentBoard = useBoards((state) => state.touchRecentBoard)
    const loadCategories = useSidebar((state) => state.loadCategories)

    const [searching, setSearching] = useState(false)
    const [creating, setCreating] = useState(false)

    const teamId = params.teamId || storeTeamId

    useEffect(() => {
        if (params.teamId && params.teamId !== storeTeamId) {
            setTeamId(params.teamId)
        }
    }, [params.teamId, setTeamId, storeTeamId])

    const realBoards = useMemo(
        () => Object.values(boards).filter((b) => !b.isTemplate && b.deleteAt === 0),
        [boards],
    )

    const board = params.boardId ? boards[params.boardId] : realBoards[0]

    useEffect(() => {
        if (board) {
            touchRecentBoard(board.id)
            void loadBoardData(board.id)
            void loadCategories(teamId)
        }
    }, [board?.id, loadCategories, teamId, touchRecentBoard])

    // deep link to a board without a team segment: normalise to /team/:id/...
    useEffect(() => {
        if (!params.teamId && board && teamId && !window.location.pathname.startsWith('/board')) {
            navigate(buildBoardPath({teamId, boardId: board.id, viewId: params.viewId, cardId: params.cardId}), {replace: true})
        }
    }, [board, navigate, params.cardId, params.teamId, params.viewId, teamId])

    const views = useMemo(() => selectViewsOfBoard(viewsById, board?.id), [viewsById, board?.id])
    const view = useMemo(() => selectActiveView(viewsById, board?.id, params.viewId), [viewsById, board?.id, params.viewId])

    const cards = useMemo(
        () => (board ? Object.values(cardsById).filter((card) => card.boardId === board.id) : []),
        [board?.id, cardsById],
    )

    const showCard = useCallback((cardId: string | undefined): void => {
        if (!board) {
            return
        }
        navigate(buildBoardPath({teamId, boardId: board.id, viewId: view?.id, cardId}))
    }, [board, navigate, teamId, view?.id])

    useEffect(() => {
        const onKey = (event: KeyboardEvent): void => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault()
                setSearching(true)
            }
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [])

    if (!board) {
        return (
            <Workspace activeBoardId='' activeViewId={undefined} readOnly={false}>
                <EmptyWorkspace onCreate={() => setCreating(true)}/>
                {creating && <NewBoardDialog onClose={() => setCreating(false)}/>}
            </Workspace>
        )
    }

    return (
        <Workspace
            activeBoardId={board.id}
            activeViewId={view?.id}
            readOnly={false}
        >
            <CenterPanel
                board={board}
                views={views}
                view={view}
                cards={cards}
                usersById={usersById}
                teamId={teamId}
                readonly={false}
                cardId={params.cardId}
                showCard={showCard}
                openSearch={() => setSearching(true)}
            />
            {searching && <SearchDialog onClose={() => setSearching(false)}/>}
        </Workspace>
    )
}

function EmptyWorkspace({onCreate}: {onCreate: () => void}): ReactElement {
    return (
        <div className='flex flex-1 flex-col items-center justify-center gap-3 text-center'>
            <Icon name='view-board' size={40} className='opacity-30'/>
            <div className='text-lg font-semibold'>还没有看板</div>
            <div className='max-w-[380px] text-sm opacity-60'>
                创建第一个看板，即可体验看板、表格、画廊和日历四种视图。
            </div>
            <button type='button' className='Button Button--primary' onClick={onCreate}>+ 新建看板</button>
        </div>
    )
}
